import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue"
import Dropzone from "@/components/widgets/input/Dropzone.vue"
import {
  MissionDocumentInput,
  MissionDocumentTypeEnum,
  CreateMissionDocumentMutationMutation,
  CreateMissionDocumentMutation,
  MissionDocument,
  UpdateMissionDocumentMutation,
  UpdateMissionDocumentMutationMutation,
  MissionLifecycleFragmentFragment,
  UploadFileInput,
  ConsultantMissionLifecycle,
} from "@/gql"
import { RefetchQueryDescription } from "apollo-client/core/watchQueryOptions"
import { useNotification } from "@/hooks/useNotification"
import FileUpload from "@/components/widgets/input/FileUpload.vue"
import { useMutation } from "@/hooks/useMutation"
import { useAccountContext } from "@/hooks/useAccountContext"

type PropTypes = {
  missionLifecycle: MissionLifecycleFragmentFragment | ConsultantMissionLifecycle
  refetchQueries: RefetchQueryDescription
  document: MissionDocument | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'UploadFileForm',
  props: {
    missionLifecycle: null,
    refetchQueries: null,
    document: null
  },
  emits: ["close"],
  setup(__props: any, { expose, emit }) {

const { missionLifecycle, refetchQueries, document } = __props as {
  missionLifecycle: MissionLifecycleFragmentFragment | ConsultantMissionLifecycle
  refetchQueries: RefetchQueryDescription
  document: MissionDocument | null
}

const { isConsultant } = useAccountContext()





const { mutate } = useMutation()
const { addSuccess, addMutationError } = useNotification()

const defaultFormValues = {
  name: "",
  uploadFile: null,
  documentType: MissionDocumentTypeEnum.MissionDocument,
  note: "",
}

const form = ref<MissionDocumentInput>({
  ...defaultFormValues,
})

const observer = ref(null)
const isSubmitting = ref(false)

const documentTypes = computed(() =>
  [
    {
      text: "Mission document",
      value: MissionDocumentTypeEnum.MissionDocument,
    },
    {
      text: "Contract",
      value: MissionDocumentTypeEnum.Contract,
    },
    {
      text: "Requirement",
      value: MissionDocumentTypeEnum.Requirements,
    },
    {
      text: "Revision/Feedback",
      value: MissionDocumentTypeEnum.RevisionOrFeedback,
    },
    !isConsultant && {
      text: "Terms of Reference",
      value: MissionDocumentTypeEnum.TermsOfReference,
    },
  ].filter(Boolean)
)

const dropzone = ref(null)

const resetForm = () => {
  form.value = {
    ...defaultFormValues,
  }

  observer.value && (observer.value as any)?.reset()
  dropzone.value && (dropzone.value as any)?.reset()
}

const onClose = () => {
  resetForm()
  emit("close")
}

const createMissionDocument = async () => {
  if (!missionLifecycle.prn) return

  form.value.name = form.value.uploadFile?.name
  const result = await mutate<CreateMissionDocumentMutationMutation>({
    mutation: CreateMissionDocumentMutation,
    variables: {
      missionLifecycleId: missionLifecycle.prn,
      missionDocumentInput: form.value,
    },
    refetchQueries: refetchQueries,
    onMutationError: (error) => {
      addMutationError(error)
    },
  })

  if (result?.data?.createMissionDocument?.document) {
    addSuccess("File uploaded successfully!")
    onClose()
  }
}

const updateMissionDocument = async () => {
  if (!document?.prn) return

  const payload = {
    documentType: form.value.documentType,
    note: form.value.note,
    name: form.value.name,
  }

  const result = await mutate<UpdateMissionDocumentMutationMutation>({
    mutation: UpdateMissionDocumentMutation,
    variables: {
      missionDocumentId: document.prn,
      missionDocumentInput: payload,
    },
    refetchQueries: refetchQueries,
  })

  if (result?.data?.updateMissionDocument?.document) {
    addSuccess("File updated successfully!")
    onClose()
  }
}

const onSubmitFileUpload = async () => {
  const validity = await (observer.value as any)?.validateWithInfo()

  if (!validity.isValid) return

  isSubmitting.value = true

  if (document?.id) await updateMissionDocument()
  else await createMissionDocument()

  isSubmitting.value = false
}

watch(
  () => document,
  (document) => {
    if (document) {
      form.value = {
        name: document.uploadFile.name,
        uploadFile: document.uploadFile as unknown as UploadFileInput,
        documentType: document.documentType as MissionDocumentTypeEnum,
        note: document.note ?? "",
      }
    } else {
      resetForm()
    }
  },
  { immediate: true, deep: true }
)

expose({ resetForm })

return { __sfc: true,isConsultant, emit, mutate, addSuccess, addMutationError, defaultFormValues, form, observer, isSubmitting, documentTypes, dropzone, resetForm, onClose, createMissionDocument, updateMissionDocument, onSubmitFileUpload, Dropzone, FileUpload }
}

})